// PR-DEL-TRIPS-DRIVERS MAY 11 — reusable delete confirmation drawer.
// Mirrors the vehicle delete modal (PR-VEH-FIX cfbb5ad + PR-FD 60c6c24).
//
// Caller passes a fetcher that, given a row's id, returns dependency counts
// or null (for bulk where counts are summed). The drawer:
//   - shows the dependency list (amber)
//   - exposes a "Force delete" checkbox to super_admin (the row server-side
//     gates that too)
//   - requires reason >= 3 chars + typing DELETE
//   - calls the supplied performDelete with { ids, force, reason }
//   - surfaces server's 409 `counts` / `reason` payload as a friendly error.
import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export interface DepCounts {
  [key: string]: number | string | null | undefined;
}

export interface DeleteConfirmDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "trip"/"driver"/"vehicle" — used in copy. */
  entityKind: string;
  /** Display label for the row(s) being deleted ("TRP-000123" or "5 selected drivers"). */
  label: string;
  /** Counts to render before any save attempt, if known up-front. */
  initialCounts?: DepCounts;
  /** "force-only" warning text shown above the force checkbox. */
  forceWarning?: string;
  /** Called with { force, reason }; throws on failure (drawer will surface). */
  performDelete: (args: { force: boolean; reason: string }) => Promise<{ counts?: DepCounts } | void>;
  /** Optional: called after a successful deletion (close + refresh list). */
  onDeleted?: () => void;
}

export function DeleteConfirmDrawer({
  open, onOpenChange,
  entityKind, label, initialCounts, forceWarning,
  performDelete, onDeleted,
}: DeleteConfirmDrawerProps) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<DepCounts | null>(initialCounts ?? null);

  useEffect(() => {
    if (!open) return;
    setReason(''); setConfirm(''); setForce(false); setError(null);
    setCounts(initialCounts ?? null);
  }, [open, initialCounts]);

  /**
   * What gets stored when a super admin leaves the box empty. The server
   * refuses a delete without a reason, and it is right to - an audit row that
   * says nothing is barely better than none. This one at least says who and
   * from where.
   */
  const DEFAULT_REASON = 'Deleted by super admin';

  async function go(withForce = force) {
    // A super admin skips the typing. Everyone else does not: for them this
    // drawer is the last thing between a live record and its removal.
    if (!isSuperAdmin && confirm !== 'DELETE') { setError('Type DELETE to confirm'); return; }
    if (!isSuperAdmin && reason.trim().length < 3) { setError('Reason is required (3+ chars)'); return; }
    const finalReason = reason.trim().length >= 3 ? reason.trim() : DEFAULT_REASON;
    setBusy(true); setError(null);
    try {
      await performDelete({ force: withForce, reason: finalReason });
      onDeleted?.();
      onOpenChange(false);
    } catch (e) {
      const errResp = (e as { response?: { data?: { reason?: string; counts?: DepCounts } } })?.response?.data;
      if (errResp?.counts) setCounts(errResp.counts);
      setError(errResp?.reason ?? extractErrorMessage(e));
    } finally { setBusy(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle className="text-rose-700 dark:text-rose-400 inline-flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" /> Delete {entityKind}?
          </SheetTitle>
          <SheetDescription>
            <span className="font-mono text-xs">{label}</span>
            <br />This action cannot be undone.
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Reason {isSuperAdmin ? '(optional)' : '(required)'}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={`Why is this ${entityKind} being deleted?`}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          {/* The typed confirmation is for everyone except a super admin, who
              is expected to be clearing rows deliberately and often. */}
          {!isSuperAdmin && (
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Type DELETE to confirm</label>
              <Input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="DELETE"
                className="font-mono"
              />
            </div>
          )}
          {error && (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">
              {error}
            </div>
          )}
          {counts && (
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-xs space-y-2">
              <div className="font-medium text-amber-700 dark:text-amber-400">
                Dependencies that block standard delete:
              </div>
              <ul className="space-y-0.5 text-amber-700 dark:text-amber-400">
                {Object.entries(counts).filter(([, v]) => typeof v === 'number' && v > 0).map(([k, v]) => (
                  <li key={k}>· {String(k).replace(/_/g, ' ')}: {String(v)}</li>
                ))}
              </ul>
              {isSuperAdmin && (
                <div className="pt-2 border-t border-amber-500/30 space-y-2">
                  <div className="text-rose-700 dark:text-rose-400">
                    {forceWarning ? <em>{forceWarning}</em> : <em>For cleanup only — do NOT use on live data.</em>}
                  </div>
                  {/* One press, where the refusal appeared. The old flow was:
                      press Delete, read the 409, find a tick-box inside this
                      warning, tick it, press Delete again. */}
                  <Button
                    variant="default"
                    onClick={() => { setForce(true); void go(true); }}
                    disabled={busy}
                    className="bg-rose-600 hover:bg-rose-700 text-white gap-1 w-full"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    Delete anyway — cascade through everything above
                  </Button>
                </div>
              )}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground italic">
            {isSuperAdmin
              ? 'The server still refuses a delete that has dependencies — you will be offered to cascade through them.'
              : 'The server will refuse deletion when dependencies exist. Force is super_admin only and irreversible.'}
          </p>
        </div>
        <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            variant="default"
            onClick={() => void go()}
            disabled={busy || (!isSuperAdmin && (confirm !== 'DELETE' || reason.trim().length < 3))}
            className="bg-rose-600 hover:bg-rose-700 text-white gap-1"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {force ? 'Force delete permanently' : 'Delete permanently'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
